<template>
    <div class="print-view bg-white min-h-screen w-full relative text-black">
        <div class="print:hidden sticky top-0 bg-gray-100 p-4 shadow-md flex justify-between items-center z-50">
            <h1 class="text-xl font-bold">{{ playlistStore.state.playlist?.name || 'Playlist Document' }}</h1>
            <div class="flex gap-2">
                <button @click="closeTab" class="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400">Close</button>
                <button @click="printDocument" class="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 font-bold">Print</button>
            </div>
        </div>
        
        <div v-if="playlistStore.state.loading" class="flex justify-center p-10">
            <div class="text-xl text-gray-500">Loading playlist...</div>
        </div>
        
        <div v-else-if="playlistStore.state.playlist" class="print-content">
            <div v-for="(song, index) in playlistStore.state.playlist.songs" :key="song.id || index" class="song-print-container page-break-after p-8 print:p-0">
                
                <div class="text-center font-bold text-3xl mb-2 mt-4">{{ song.userData?.title || song.data?.title }}</div>
                <div class="text-center text-lg text-gray-600 mb-8">{{ song.userData?.artist?.name || song.data?.artist?.name }}</div>
                
                <div class="relative flex flex-col items-center w-full" :class="{ 'hide-song-map': !showSongMap }">
                    <UserNotes v-if="showNotes" :storeKey="'print_' + song.id" :playlistId="route.params.id" :songId="song.id" />
                    
                    <SongCanvas v-if="showNotes" 
                        class="flex flex-col h-full w-full items-center song-canvas z-[30]" 
                        targetSelector=".song-viewer"
                        :enabled="false"
                        :show-controls="false"
                        :room-id="getRoomId(song.id)"
                        :user-id="userStore.state.userInfo?.id"
                        :playlistId="route.params.id"
                        :songId="song.id"
                        :zoomValue="1"
                        :content-ready="true"
                    />
                    
                    <SongViewer 
                        :content="song.modifiedLyric || song.data?.content || song.data?.lyric"
                        :border="false"
                        :class="[columnView]"
                        :zoom="1"
                    />
                </div>
                
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import playlistStore from "@/store/playlist.store";
import chordsStore from "@/store/chords.store";
import userStore from "@/store/user.store";
import SongViewer from "@/components/editor/SongViewer.vue";
import UserNotes from "@/components/common/elements/UserNotes.vue";
import SongCanvas from "@/components/common/elements/SongCanvas.vue";

const route = useRoute();

const showNotes = computed(() => route.query.notes !== 'false');
const showSongMap = computed(() => route.query.songMap !== 'false');
const columnView = computed(() => route.query.columns || 'column-1');
const showChords = computed(() => route.query.chords !== 'false');
const orientation = computed(() => route.query.orientation || 'portrait');

const printDocument = () => {
    window.print();
};

const closeTab = () => {
    window.close();
};

const getRoomId = (songId) => {
    return `${route.params.id}-${songId}`;
};

onMounted(async () => {
    chordsStore.dispatch('visible', showChords.value);
    
    // Apply orientation and page settings via CSS page rule dynamically
    // margin: 0 removes the browser's default headers and footers (which contain the URL)
    const style = document.createElement('style');
    style.innerHTML = `@page { size: auto ${orientation.value}; margin: 0; }`;
    document.head.appendChild(style);
    
    if (!playlistStore.state.playlist || playlistStore.state.playlist.id !== route.params.id) {
        await playlistStore.dispatch("load", { id: route.params.id });
    }
});
</script>

<style scoped>
.print-view {
    background-color: white !important;
}

@media print {
    .print\:hidden {
        display: none !important;
    }
    .page-break-after {
        page-break-after: always;
        break-after: page;
    }
    .print-content {
        padding-top: 20mm; /* Adds the top margin for the content */
        padding-bottom: 20mm;
    }
    body, .print-view {
        background-color: white !important;
    }
}
</style>
